import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { CertRegistration } from './listCategoryCertRequirements';
import type { WriteRequestOptions } from '../requestOptions';

/**
 * Response payload from POST /api/integrations/tiktok/{sub_id}/certifications.
 * The `registration` block reflects the row state after the producer command
 * has run synchronously — `status` is terminal (`registered` or `failed`),
 * not `pending`.
 */
export interface RegisterCertificationData {
  subscription_id: number;
  channel_name: string;
  registration: CertRegistration & { channel_cert_type_id: string };
}

/**
 * POST /api/integrations/tiktok/{subscription_id}/certifications
 *
 * Inserts (or refreshes) a `channel_certifications` row keyed on the natural
 * unique (subscription, channel, cert_type, asset), then synchronously runs
 * `asturIntegrationTikTok:registerCertification` to upload the asset bytes
 * to TikTok and persist the upload response id. Caller's caller is expected
 * to have first POSTed the user's selected file to Astur `/assets` to
 * obtain `asturAssetId`.
 *
 * Returns 200 on registered, 502 on failed (the registration row is still
 * persisted with `status = 'failed'`, so the UI can surface the error state).
 */
export async function registerCertification(
  scope: RequestScope,
  args: {
    subscriptionId: number;
    asturAssetId: number;
    channelCertTypeId: string;
  },
  options: WriteRequestOptions
): Promise<ChannelApiResponse<RegisterCertificationData>> {
  try {
    if (!args.subscriptionId || args.subscriptionId < 1) {
      throw new Error('Valid subscriptionId is required');
    }
    if (!args.asturAssetId || args.asturAssetId < 1) {
      throw new Error('Valid asturAssetId is required');
    }
    const certTypeId = (args.channelCertTypeId ?? '').trim();
    if (!certTypeId) {
      throw new Error('channelCertTypeId is required');
    }

    const res = await scopedChannelApi(scope).post<RegisterCertificationData>(
      `integrations/tiktok/${args.subscriptionId}/certifications`,
      {
        astur_asset_id: args.asturAssetId,
        channel_cert_type_id: certTypeId,
      },
      options
    );

    return { data: res.data, status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}
